import { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Permission } from '@brandschutz/shared';
import { AppShell } from '@/components/layout/AppShell';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';
import { LoginPage } from '@/pages/LoginPage';

// Alle In-Shell-Seiten werden lazy geladen → ein eigener Chunk pro Route,
// schlanker Initial-Load. Die Suspense-Boundary sitzt in AppShell um den
// <Outlet/>, damit Sidebar/Topbar beim Seitenwechsel stehen bleiben.
// LoginPage bleibt eager (Unauth-Entry, klein, kein Shell-Kontext).
const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const ProjectListPage = lazy(() =>
  import('@/pages/ProjectListPage').then((m) => ({ default: m.ProjectListPage })),
);
const ProjectDetailPage = lazy(() =>
  import('@/pages/ProjectDetailPage').then((m) => ({ default: m.ProjectDetailPage })),
);
const AiProposalsPage = lazy(() =>
  import('@/pages/AiProposalsPage').then((m) => ({ default: m.AiProposalsPage })),
);
const NewProjectWizardPage = lazy(() =>
  import('@/pages/NewProjectWizardPage').then((m) => ({ default: m.NewProjectWizardPage })),
);
const CustomerListPage = lazy(() =>
  import('@/pages/CustomerListPage').then((m) => ({ default: m.CustomerListPage })),
);
const CustomerDetailPage = lazy(() =>
  import('@/pages/CustomerDetailPage').then((m) => ({ default: m.CustomerDetailPage })),
);
const LiegenschaftDetailPage = lazy(() =>
  import('@/pages/LiegenschaftDetailPage').then((m) => ({ default: m.LiegenschaftDetailPage })),
);
const FreimeldungPage = lazy(() =>
  import('@/pages/FreimeldungPage').then((m) => ({ default: m.FreimeldungPage })),
);
const MangelVorlagenPage = lazy(() =>
  import('@/pages/MangelVorlagenPage').then((m) => ({ default: m.MangelVorlagenPage })),
);
const DocumentInboxPage = lazy(() =>
  import('@/pages/DocumentInboxPage').then((m) => ({ default: m.DocumentInboxPage })),
);
const AdminUsersPage = lazy(() =>
  import('@/pages/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })),
);
const AdminFeatureFlagsPage = lazy(() =>
  import('@/pages/AdminFeatureFlagsPage').then((m) => ({ default: m.AdminFeatureFlagsPage })),
);
const AdminAiUsagePage = lazy(() =>
  import('@/pages/AdminAiUsagePage').then((m) => ({ default: m.AdminAiUsagePage })),
);
const AdminRulesPage = lazy(() =>
  import('@/pages/AdminRulesPage').then((m) => ({ default: m.AdminRulesPage })),
);
const AdminAuditLogPage = lazy(() =>
  import('@/pages/AdminAuditLogPage').then((m) => ({ default: m.AdminAuditLogPage })),
);
const AdminFeedbackPage = lazy(() =>
  import('@/pages/AdminFeedbackPage').then((m) => ({ default: m.AdminFeedbackPage })),
);
const AdminBrandingPage = lazy(() =>
  import('@/pages/AdminBrandingPage').then((m) => ({ default: m.AdminBrandingPage })),
);
const PricingPage = lazy(() =>
  import('@/pages/PricingPage').then((m) => ({ default: m.PricingPage })),
);
const SettingsPage = lazy(() =>
  import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const NotFoundPage = lazy(() =>
  import('@/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);
const ContactListPage = lazy(() =>
  import('@/skills/contact-management').then((m) => ({ default: m.ContactListPage })),
);
const ContactImportWizard = lazy(() =>
  import('@/skills/data-import').then((m) => ({ default: m.ContactImportWizard })),
);
const HelpCenterPage = lazy(() =>
  import('@/skills/help-center').then((m) => ({ default: m.HelpCenterPage })),
);
const HelpVideosPage = lazy(() =>
  import('@/skills/help-center').then((m) => ({ default: m.HelpVideosPage })),
);
const ManualPage = lazy(() =>
  import('@/skills/help-center').then((m) => ({ default: m.ManualPage })),
);
const TemplatesPage = lazy(() =>
  import('@/skills/templates').then((m) => ({ default: m.TemplatesPage })),
);
const PrueferListPage = lazy(() =>
  import('@/skills/pruefer').then((m) => ({ default: m.PrueferListPage })),
);
const MailInboxPage = lazy(() =>
  import('@/skills/mail-inbound').then((m) => ({ default: m.MailInboxPage })),
);
const TrainingPage = lazy(() =>
  import('@/skills/training').then((m) => ({ default: m.TrainingPage })),
);
const LessonPage = lazy(() => import('@/skills/training').then((m) => ({ default: m.LessonPage })));
// floor-plan-editor: Konva + pdfjs ~450 KB — eigener Chunk, erst bei Route-Match.
const MarkupPage = lazy(() =>
  import('@/skills/floor-plan-editor').then((m) => ({ default: m.MarkupPage })),
);

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Freimeldung per QR-Etikett — die einzige Seite ohne Anmeldung, und
          deshalb bewusst AUSSERHALB der geschützten Hülle: kein AppShell,
          keine Navigation, kein Weg tiefer in die Anwendung. Wer den Aufkleber
          scannt, ist der Hausmeister des Kunden, nicht unser Personal. */}
      <Route path="/m/:token" element={<FreimeldungPage />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        {/* Dashboard braucht keine eigene Permission — jede angemeldete Rolle
            darf es sehen; die einzelnen Kacheln/Widgets gaten sich selbst. */}
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route
          path="/projects"
          element={
            <ProtectedRoute permission={Permission.PROJECT_READ}>
              <ProjectListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/new"
          element={
            <ProtectedRoute permission={Permission.PROJECT_CREATE}>
              <NewProjectWizardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:id"
          element={
            <ProtectedRoute permission={Permission.PROJECT_READ}>
              <ProjectDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-proposals"
          element={
            <ProtectedRoute permission={Permission.AI_USE}>
              <AiProposalsPage />
            </ProtectedRoute>
          }
        />
        {/* Markup-Editor: das Backend verlangt zum Speichern von Szenen/Shapes
            PROJECT_UPDATE (floor-plan-editor/routes.ts). Ohne diese Permission
            waere der Editor nur ein Betrachter, der beim ersten Speichern in
            einen 403 laeuft — daher gaten wir die Route auf PROJECT_UPDATE. */}
        <Route
          path="/projects/:id/markup"
          element={
            <ProtectedRoute permission={Permission.PROJECT_UPDATE}>
              <MarkupPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:id/markup/:sceneId"
          element={
            <ProtectedRoute permission={Permission.PROJECT_UPDATE}>
              <MarkupPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/customers"
          element={
            <ProtectedRoute permission={Permission.CUSTOMER_READ}>
              <CustomerListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/customers/:id"
          element={
            <ProtectedRoute permission={Permission.CUSTOMER_READ}>
              <CustomerDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/liegenschaften/:id"
          element={
            <ProtectedRoute permission={Permission.LIEGENSCHAFT_READ}>
              <LiegenschaftDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/contacts"
          element={
            <ProtectedRoute permission={Permission.CONTACT_READ}>
              <ContactListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/imports/contacts"
          element={
            <ProtectedRoute permission={Permission.CONTACT_CREATE}>
              <ContactImportWizard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/documents"
          element={
            <ProtectedRoute permission={Permission.DOCUMENT_READ}>
              <DocumentInboxPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pruefer"
          element={
            <ProtectedRoute permission={Permission.PRUEFER_READ}>
              <PrueferListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mail"
          element={
            <ProtectedRoute permission={Permission.MAIL_INBOX_READ}>
              <MailInboxPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/templates"
          element={
            <ProtectedRoute permission={Permission.PROJECT_READ}>
              <TemplatesPage />
            </ProtectedRoute>
          }
        />
        {/* Textbibliothek der Maengel: lesen darf, wer Maengel liest; pflegen
            gatet die Seite selbst ueber MANGEL_VORLAGE_MANAGE. */}
        <Route
          path="/mangel-vorlagen"
          element={
            <ProtectedRoute permission={Permission.MANGEL_READ}>
              <MangelVorlagenPage />
            </ProtectedRoute>
          }
        />
        {/* Hilfe/Schulung + eigene Einstellungen sind bewusst fuer jede
            angemeldete Rolle offen — sie enthalten keine Projektdaten. */}
        <Route path="/hilfe" element={<HelpCenterPage />} />
        <Route path="/hilfe/videos" element={<HelpVideosPage />} />
        <Route path="/hilfe/schulung" element={<TrainingPage />} />
        <Route path="/hilfe/schulung/:moduleSlug/:lessonSlug" element={<LessonPage />} />
        {/* Handbuch: statische Segmente ranken in React Router hoeher als das
            :slug darunter — die Reihenfolge hier ist nur der Lesbarkeit wegen. */}
        <Route path="/hilfe/handbuch" element={<ManualPage />} />
        <Route path="/hilfe/handbuch/:slug" element={<ManualPage />} />
        <Route path="/hilfe/:slug" element={<HelpCenterPage />} />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute permission={Permission.USER_MANAGE}>
              <AdminUsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/features"
          element={
            <ProtectedRoute permission={Permission.FEATURE_FLAG_ADMIN}>
              <AdminFeatureFlagsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/feedback"
          element={
            <ProtectedRoute permission={Permission.FEEDBACK_MANAGE}>
              <AdminFeedbackPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/rules"
          element={
            <ProtectedRoute permission={Permission.FEATURE_FLAG_ADMIN}>
              <AdminRulesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/ai-usage"
          element={
            <ProtectedRoute permission={Permission.FEATURE_FLAG_ADMIN}>
              <AdminAiUsagePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/audit"
          element={
            <ProtectedRoute permission={Permission.AUDIT_VIEW_ALL}>
              <AdminAuditLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/branding"
          element={
            <ProtectedRoute permission={Permission.FEATURE_FLAG_ADMIN}>
              <AdminBrandingPage />
            </ProtectedRoute>
          }
        />
        {/* Preise sind Vertriebsmaterial — nur fuer die Rolle, die auch
            Benutzer verwaltet. Kein Sachbearbeiter braucht sie, und niemand
            soll sie versehentlich im Beisein eines Kunden aufschlagen. */}
        <Route
          path="/preise"
          element={
            <ProtectedRoute permission={Permission.USER_MANAGE}>
              <PricingPage />
            </ProtectedRoute>
          }
        />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
